import React, { useState } from 'react';
import { X, Search, Trash2, Image as ImageIcon, Loader2, Upload, Plus } from 'lucide-react';

interface UploadedImage {
  id: string;
  url: string;
  name: string;
  uploadedAt: Date;
}

interface UploadedPhotosBrowserProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectImage: (imageUrl: string) => void;
  uploadedImages: UploadedImage[];
  onDeleteImage?: (imageId: string) => void;
  onUploadImages?: () => void;
  isLoading?: boolean;
  isUploading?: boolean;
  isSelectionMode?: boolean; // true when selecting for a specific section
}

export default function UploadedPhotosBrowser({ 
  isOpen, 
  onClose, 
  onSelectImage, 
  uploadedImages,
  onDeleteImage,
  onUploadImages,
  isLoading = false,
  isUploading = false,
  isSelectionMode = false
}: UploadedPhotosBrowserProps) {
  const [searchQuery, setSearchQuery] = useState('');

  if (!isOpen) return null;

  const filteredImages = uploadedImages.filter(img =>
    img.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleImageSelect = (imageUrl: string) => {
    onSelectImage(imageUrl);
    onClose();
  };

  const handleDeleteImage = (e: React.MouseEvent, imageId: string) => {
    e.stopPropagation();
    if (onDeleteImage) {
      onDeleteImage(imageId);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4">
      <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">My Photo Library</h2>
              <p className="text-gray-600 mt-1">
                {uploadedImages.length === 0 
                  ? 'No photos uploaded yet' 
                  : `${uploadedImages.length} photo${uploadedImages.length === 1 ? '' : 's'} in your library`
                }
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={onUploadImages}
                disabled={isUploading}
                className={`px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2 ${
                  isUploading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Uploading...</span>
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    <span>Upload Photos</span>
                  </>
                )}
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search uploaded photos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
        </div>

        {/* Images Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-full">
              <Loader2 className="h-8 w-8 text-purple-600 animate-spin mb-4" />
              <p className="text-gray-600">Loading your images...</p>
            </div>
          ) : filteredImages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <ImageIcon className="h-16 w-16 mb-4 text-gray-300" />
              <p className="text-lg font-medium">
                {searchQuery ? 'No images found matching your search' : 'Your photo library is empty'}
              </p>
              {!searchQuery && (
                <>
                  <p className="text-sm mt-2 mb-4">
                    Upload photos to use in your email templates
                  </p>
                  <button
                    onClick={onUploadImages}
                    disabled={isUploading}
                    className={`px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2 ${
                      isUploading ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {isUploading ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span>Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Plus className="h-5 w-5" />
                        <span>Upload Your First Photo</span>
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {filteredImages.map((image) => (
                <div
                  key={image.id}
                  onClick={() => isSelectionMode && handleImageSelect(image.url)}
                  className={`group relative rounded-lg overflow-hidden border-2 border-gray-200 hover:border-purple-500 transition-all hover:shadow-lg aspect-square ${
                    isSelectionMode ? 'cursor-pointer' : ''
                  }`}
                >
                  <img
                    src={image.url}
                    alt={image.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <p className="text-white text-sm font-medium truncate">{image.name}</p>
                      <p className="text-white/80 text-xs">
                        {new Date(image.uploadedAt).toLocaleDateString()}
                      </p>
                    </div>
                    {onDeleteImage && (
                      <button
                        onClick={(e) => handleDeleteImage(e, image.id)}
                        className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  {isSelectionMode && (
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                      <div className="bg-purple-600 text-white px-4 py-2 rounded-lg font-medium">
                        Select Image
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
